import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Text, Table, type ColumnConfig, type SortState } from '@socrait/react';

import { ErrorBox } from '~/components/ErrorBox';
import {
  useSchoolStudentsQuery,
  type SchoolStudent,
  type SchoolStudentsSortBy,
} from '~/services/administrator/useSchoolStudentsQuery';

import { AdminLayout } from '~/layouts/AdminLayout';

/** Column index → backend sort key. Keep in lock-step with the COLUMNS order. */
const SORT_KEYS: SchoolStudentsSortBy[] = ['voiceName', 'periodName', 'teacherName', 'periodShortName'];

/** Narrow fixed-width column for the numeric period; the rest of the table flexes. */
const PERIOD_COLUMN_WIDTH = 100;

const COLUMNS: ColumnConfig<SchoolStudent>[] = [
  {
    width: { type: 'flex', flex: 2 },
    header: 'Voice Name',
    render: (s) => <Text>{s.voiceName}</Text>,
    sortable: true,
  },
  {
    width: { type: 'flex', flex: 3 },
    header: 'Class',
    render: (s) => <Text>{s.periodName}</Text>,
    sortable: true,
  },
  {
    width: { type: 'flex', flex: 2 },
    header: 'Teacher',
    render: (s) => <Text>{s.teacherName}</Text>,
    sortable: true,
  },
  {
    width: { type: 'fixed', width: PERIOD_COLUMN_WIDTH },
    align: 'right',
    header: 'Period',
    render: (s) => <Text>{s.periodShortName}</Text>,
    sortable: true,
  },
];

export function StudentsPage() {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortState>(null);

  const sortBy = sort != null ? SORT_KEYS[sort.columnIndex] : undefined;
  const sortDirection = sort?.direction;

  const { data, error, status, fetchNextPage, isFetchingNextPage, hasNextPage } = useSchoolStudentsQuery({
    sortBy,
    sortDirection,
  });

  // Show the loading skeleton only on initial load. Background refetches keep
  // the existing rows visible — flipping back to skeleton on every refetch
  // (e.g. window-focus) would be jarring once the table is populated.
  const isLoading = status === 'pending';

  const students = useMemo(() => data?.pages.flatMap((page) => page.students) ?? [], [data?.pages]);

  const handleSortChange = useCallback((next: SortState) => {
    // Sort changes invalidate the cursor — TanStack Query refetches from page 1
    // automatically because `sortBy`/`sortDirection` are part of the query key.
    setSort(next);
  }, []);

  const handleRowPress = useCallback(
    (item: SchoolStudent) => {
      // Deep-link uses period short_name + student voice_name so the URL is
      // human-readable and shareable. The detail page resolves these back to
      // ids by looking them up in the same school-students list.
      navigate(`/students/${encodeURIComponent(item.slug)}`);
    },
    [navigate],
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <AdminLayout activeTab="students" scrollable={false}>
      {!isLoading && error && <ErrorBox text="Failed to load students. Please try reloading." />}
      <Table
        data={students}
        columns={COLUMNS}
        keyExtractor={(item) => `${item.periodId}-${item.studentId}`}
        isLoading={isLoading}
        sort={sort}
        onSortChange={handleSortChange}
        onRowPress={handleRowPress}
        hasMore={!!hasNextPage}
        onLoadMore={handleLoadMore}
        accessibilityLabel="Students table"
      />
    </AdminLayout>
  );
}
